import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Tabs,
  Tab,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { ResponsiveModal, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { CampaignStats } from '../types';
import GeneralStatsTab from './GeneralStatsTab';
import FansTab from './FansTab';
import ClicksConversionTab from './ClicksConversionTab';

interface CampaignStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: CampaignStats | null;
}

const CampaignStatsModal: React.FC<CampaignStatsModalProps> = ({ isOpen, onClose, campaign }) => {
  const { t } = useTranslations();
  const [selectedTab, setSelectedTab] = useState<'general' | 'fans' | 'clicks'>('general');

  if (!campaign) return null;

  // Prepare data for GeneralStatsTab
  const totalTransactions =
    campaign.transactions_statistic?.total ??
    campaign.transactions_statistic_total ??
    campaign.revenue ??
    0;

  const generalStatsData = {
    clicks: campaign.clicks || 0,
    subscribers: campaign.fans || 0,
    totalTransactions,
    revenue: totalTransactions,
    ctr: 200, // TODO: Calculate or get from API
    arpu: 2.0, // TODO: Calculate or get from API
    cvpc: 4.0, // TODO: Calculate or get from API
    rpc: 1.16, // TODO: Calculate or get from API
  };

  const fansData = {
    paidSubscription: { count: 0, percentage: 0 },
    recurringSubscription: { count: 0, percentage: 0 },
    purchases: { count: 0, percentage: 0 },
    purchases500: { count: 0, percentage: 0 },
    messages3: { count: 70, percentage: 46.36 }, // TODO: Get from API
  };

  const campaignId = campaign.id.slice(-4);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      classNames={{
        base: 'md:max-w-[37.5rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent className="rounded-4">
        {/* Header */}
        <ModalHeader className="flex flex-col gap-3 px-4 pt-5 pb-0">
          <Typography variant="h5" className="font-medium text-auth-ink text-base leading-normal">
            {t('campaignsStats.modal.title').replace('{campaignId}', campaignId)}
          </Typography>
        </ModalHeader>

        {/* Tabs */}
        <div className="border-b border-[#e2ebf7] px-4">
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) => setSelectedTab(key as 'general' | 'fans' | 'clicks')}
            variant="underlined"
            classNames={{
              tabList: 'gap-6 w-full relative rounded-none p-0 bg-transparent',
              cursor: 'w-full bg-partner h-0.5 rounded-none',
              tab: 'max-w-fit px-1 h-auto pb-3 pt-0 rounded-none data-[hover-unselected=true]:opacity-100',
              tabContent:
                'group-data-[selected=true]:text-partner text-zinc-500 font-medium text-sm leading-5',
              panel: 'p-0',
            }}
          >
            <Tab
              key="general"
              title={
                <div className="flex items-center gap-1">
                  <Icon
                    icon="solar:document-text-bold"
                    width={16}
                    height={16}
                    className={selectedTab === 'general' ? 'text-partner' : 'text-zinc-500'}
                  />
                  <span>{t('campaignsStats.modal.tabs.generalStats')}</span>
                </div>
              }
            />
            <Tab
              key="fans"
              title={
                <div className="flex items-center gap-1">
                  <Icon
                    icon="solar:users-group-rounded-bold"
                    width={16}
                    height={16}
                    className={selectedTab === 'fans' ? 'text-partner' : 'text-zinc-500'}
                  />
                  <span>{t('campaignsStats.modal.tabs.fans')}</span>
                </div>
              }
            />
            <Tab
              key="clicks"
              title={
                <div className="flex items-center gap-1">
                  <Icon
                    icon="solar:dollar-minimalistic-bold"
                    width={16}
                    height={16}
                    className={selectedTab === 'clicks' ? 'text-partner' : 'text-zinc-500'}
                  />
                  <span>{t('campaignsStats.modal.tabs.clicksConversion')}</span>
                </div>
              }
            />
          </Tabs>
        </div>

        {/* Body */}
        <ModalBody className="px-0 py-0">
          {selectedTab === 'general' && (
            <GeneralStatsTab data={generalStatsData} fansData={fansData} />
          )}
          {selectedTab === 'fans' && <FansTab />}
          {selectedTab === 'clicks' && <ClicksConversionTab />}
        </ModalBody>

        {/* Footer */}
        <ModalFooter className="px-6 pb-4 pt-4 flex items-center justify-end">
          <Button
            className="bg-partner h-10 px-3.5 py-2.5 rounded-3 min-w-[6.25rem]"
            onPress={onClose}
          >
            <Typography variant="body2" className="!text-white font-semibold text-sm leading-5">
              {t('campaignsStats.modal.close')}
            </Typography>
          </Button>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default CampaignStatsModal;
